import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Sidebar from "../../../shared/components/sidebar/Sidebar";
import Navbar from "../../../shared/components/navbar/Navbar";
import { useHttpClient } from "../../../shared/hooks/http-hook";
import { AuthContext } from "../../../shared/context/auth-context";
import ElementoOrcamento from "../components/Elemento/ElementoOrcamento";
import ElementoOrcamentoOrcamentados from "../components/Elemento/ElementoOrcamentoOrcamentados";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import validarOrcamento from "../helperFunctions/validation";
import Button from "@mui/material/Button";
import toast from "react-hot-toast";

import { useLoading } from "../../../shared/context/LoadingContext";

import "../../../shared/css/PlanoAvaliacaoOrcamento.scss";
import "../../../style/formError.scss";
import { ClinicaContext } from "../../../shared/context/clinica-context";

import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";

/**
 * Traz os tratamentos de uma avaliação para o catálogo da unidade onde se está
 * a orçamentar.
 *
 * Uma avaliação feita numa unidade pode ser orçamentada noutra — o paciente foi
 * avaliado em Assomada e volta na Praia. Mas o tratamento gravado na avaliação
 * é o do catálogo de LÁ, e cada unidade tem o seu preço. Sem esta tradução a
 * venda ficava carimbada numa unidade e cobrada ao preço da outra: o orçamento
 * saía sob a LED e a morada de Assomada a cobrar o preço da Praia.
 *
 * A correspondência é por `(designação, fornecedor)`, o mesmo critério do
 * script de transferência de cliente entre unidades. O fornecedor faz parte da
 * chave porque as próteses existem uma vez por laboratório e são tratamentos
 * distintos, com preços distintos.
 *
 * Se algum tratamento não existir na unidade de destino, NÃO se escolhe um
 * parecido: devolve-se o que faltou, para o ecrã recusar. Orçamentar aqui um
 * tratamento que aqui não se faz é pior do que não deixar orçamentar.
 *
 * @returns {{ lista: object[], emFalta: string[] }}
 */
const traduzirParaUnidadeDaSessao = (procedimentos, catalogo, unidadeSessao) => {
  // Sem unidade na sessão não há para onde traduzir — é o caso das clínicas de
  // unidade única e o de qualquer clínica antes da migração. Devolve-se tudo
  // tal e qual, e nada neste ecrã muda para elas.
  if (!unidadeSessao) return { lista: procedimentos, emFalta: [] };

  const idDe = (v) => (v && typeof v === "object" ? v._id : v);
  const emFalta = [];

  const lista = procedimentos.map((prc) => {
    const t = prc.tratamento;

    // Tratamento sem unidade: ainda não migrado. Não se toca.
    if (!t?.estabelecimento) return prc;
    if (String(idDe(t.estabelecimento)) === String(unidadeSessao)) return prc;

    const equivalente = catalogo.find(
      (c) =>
        c.designacao === t.designacao &&
        String(idDe(c.fornecedor) || "") === String(idDe(t.fornecedor) || "")
    );

    if (!equivalente) {
      emFalta.push(
        t.designacao +
          (t.fornecedor?.nome ? ` (${t.fornecedor.nome})` : "")
      );
      return prc;
    }

    return { ...prc, tratamento: equivalente };
  });

  return { lista, emFalta };
};

function NewOrcamento() {
  const { startLoading, stopLoading } = useLoading();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avaliacao, setAvaliacao] = useState();
  const [serviceList, setServiceList] = useState();
  const [procedimentosOrcamentados, setProcedimentosOrcamentados] = useState();
  // Todos os tratamentos da clínica, usados para oferecer as variantes por
  // fornecedor de cada designação na altura do orçamento.
  const [todosTratamentos, setTodosTratamentos] = useState([]);
  // Tratamentos da avaliação que não existem no catálogo desta unidade. Com
  // algum aqui, o orçamento não pode seguir — ver `traduzirParaUnidadeDaSessao`.
  const [tratamentosEmFalta, setTratamentosEmFalta] = useState([]);

  // Estados atualizados para o novo sistema
  const [tipoCobertura, setTipoCobertura] = useState("PARTICULAR");
  const [instituicaoSelecionada, setInstituicaoSelecionada] = useState(null);
  const [instituicoes, setInstituicoes] = useState([]);
  const [modalidadePagamento, setModalidadePagamento] = useState("DIRETO");

  const [subtotal, setSubtotal] = useState();
  const [subtotalCliente, setSubtotalCliente] = useState();
  const [total, setTotal] = useState();
  const [totalCobertura, setTotalCobertura] = useState(0);
  const [desconto, setDesconto] = useState(0);
  const [tipoDesconto, setTipoDesconto] = useState("$");
  const [observacoes, setObservacoes] = useState();
  const [nrTratamentosCobertos, setNrTratamentosCobertos] = useState(0);
  const [nrTratamentosNaoCobertos, setNrTratamentosNaoCobertos] = useState(0);
  const [orcamentoIsentoPagamento, setOrcamentoIsentoPagamento] =
    useState(false);
  const [caixas, setCaixas] = useState([]);
  const [categoriaBeneficiario, setCategoriaBeneficiario] = useState("");
  const [modalDescontoAberta, setModalDescontoAberta] = useState(false);
  const [descontoSegurado, setDescontoSegurado] = useState(0);
  const [descontoParticular, setDescontoParticular] = useState(0);
  const [opcaoRapidaSelecionada, setOpcaoRapidaSelecionada] = useState(null);

  const [empresaParceira, setEmpresaParceira] = useState(null);
  const [parceriaValida, setParceriaValida] = useState(false);
  const [descontoParceriaAplicado, setDescontoParceriaAplicado] =
    useState(false);

  const { sendRequest } = useHttpClient();
  const avaliacaoId = useParams().avaliacaoId;
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchInitialData = async () => {
      startLoading();
      try {
        // Buscar avaliação
        let responseData;
        try {
          responseData = await sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/avaliacoes/${avaliacaoId}`,
            "GET",
            null,
            {
              Authorization: "Bearer " + auth.token,
            }
          );
        } catch (err) {
          console.error("err", err);
        }

        // Verificar se o cliente tem empresa parceira
        if (responseData.avaliacao.cliente.empresaParceira) {
          try {
            const responseEmpresa = await sendRequest(
              `${process.env.REACT_APP_BACKEND_LINK}/api/empresasparceiras/verificar/${responseData.avaliacao.cliente.empresaParceira}`,
              "GET",
              null,
              { Authorization: "Bearer " + auth.token }
            );

            if (responseEmpresa.parceriaValida) {
              setEmpresaParceira(responseEmpresa.empresa);
              setParceriaValida(true);
            }
          } catch (err) {
            console.error("Erro ao verificar empresa parceira:", err);
          }
        }

        // Buscar caixas
        try {
          const responseCaixas = await sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/clinica/${clinica.clinica._id}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token }
          );
          setCaixas(responseCaixas.caixas);
        } catch (err) {
          console.error("err", err);
        }

        // Buscar instituições da clínica
        try {
          const responseInstituicoes = await sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/instituicoes/clinica/${clinica.clinica._id}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token }
          );
          setInstituicoes(
            responseInstituicoes.instituicoes.filter((inst) => inst.ativo)
          );
        } catch (err) {
          console.error("err", err);
        }

        // Buscar todos os tratamentos (para as variantes por fornecedor). O
        // servidor devolve só os da unidade da sessão — é este o catálogo para
        // onde os tratamentos da avaliação têm de ser traduzidos.
        let catalogoDaUnidade = [];
        try {
          const responseTratamentos = await sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentos/populated/${clinica.clinica._id}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token }
          );
          catalogoDaUnidade = responseTratamentos.tratamentos || [];
          setTodosTratamentos(catalogoDaUnidade);
        } catch (err) {
          console.error("err", err);
        }

        const newProcedimentosList =
          responseData.avaliacao.procedimentos.filter(
            (pr) => pr.orcamentado === false
          );

        setProcedimentosOrcamentados(
          responseData.avaliacao.procedimentos.filter(
            (pr) => pr.orcamentado === true
          )
        );

        /* Só as clínicas com mais do que uma unidade traduzem. Nas restantes
           `unidadeSessao` fica a null e a função devolve a lista intacta —
           nada neste ecrã muda para elas. */
        const unidadeSessao = clinica.clinica?.multiEstabelecimento
          ? auth.estabelecimento?._id || null
          : null;

        const { lista: procedimentosDaUnidade, emFalta } =
          traduzirParaUnidadeDaSessao(
            newProcedimentosList,
            catalogoDaUnidade,
            unidadeSessao
          );

        setTratamentosEmFalta(emFalta);

        const newServiceList = procedimentosDaUnidade.map((prc) => {
          return { ...prc, show: true };
        });

        const tempSubtotal = newServiceList.reduce((acc, el) => {
          return acc + el.tratamento.preco_particular;
        }, 0);

        setSubtotal(tempSubtotal);
        setSubtotalCliente(tempSubtotal);

        setTotal(
          newServiceList.reduce((acc, el) => {
            return acc + el.tratamento.preco_particular;
          }, 0)
        );

        setAvaliacao(responseData.avaliacao);
        setServiceList(newServiceList);
      } catch (err) {
        console.error("err", err);
      } finally {
        stopLoading();
      }
    };
    fetchInitialData();
  }, [
    sendRequest,
    avaliacaoId,
    auth.token,
    auth.estabelecimento?._id,
    clinica.clinica._id,
    clinica.clinica?.multiEstabelecimento,
  ]);

  // Atualizar valores quando mudar modalidade de pagamento
  useEffect(() => {
    if (instituicaoSelecionada) {
      updateValores(instituicaoSelecionada._id);
    }
  }, [modalidadePagamento]);

  // Atualizar contagem de tratamentos cobertos quando mudar instituição ou serviceList
  // Atualizar contagem de tratamentos cobertos quando mudar instituição ou serviceList
  useEffect(() => {
    if (serviceList && instituicaoSelecionada) {
      let cobertos = 0;
      let naoCobertos = 0;

      serviceList.forEach((service) => {
        if (service.show) {
          const cobertura = service.tratamento.precosPorInstituicao?.find(
            (p) => p.instituicao === instituicaoSelecionada._id
          );
          if (cobertura?.coberto) {
            cobertos++;
          } else {
            naoCobertos++;
          }
        }
      });

      setNrTratamentosCobertos(cobertos);
      setNrTratamentosNaoCobertos(naoCobertos);

      // Determinar tipo de cobertura
      if (cobertos === 0 && naoCobertos > 0) {
        // Se não há tratamentos cobertos, mudar para PARTICULAR
        setTipoCobertura("PARTICULAR");
        setInstituicaoSelecionada(null);
        setModalidadePagamento("DIRETO");
        updateValores("PARTICULAR");
      } else if (naoCobertos > 0 && cobertos > 0) {
        setTipoCobertura("MISTO");
      } else if (cobertos > 0) {
        setTipoCobertura("SEGURADO");
      }
    }
  }, [serviceList, instituicaoSelecionada]);

  const handleInstituicaoChange = (e) => {
    const value = e.target.value;
    if (value === "PARTICULAR") {
      setTipoCobertura("PARTICULAR");
      setInstituicaoSelecionada(null);
      setModalidadePagamento("DIRETO");
    } else {
      const instituicao = instituicoes.find((inst) => inst._id === value);
      setInstituicaoSelecionada(instituicao);
      setModalidadePagamento("DIRETO");
    }
    setCategoriaBeneficiario("");

    // Recalcular valores
    updateValores(value);
  };

  const submitHandler = async (contaAprovada) => {
    startLoading();
    try {
      const filteredServiceList = [];

      const formErrorMessage = validarOrcamento(serviceList);

      if (!formErrorMessage) {
        serviceList.forEach((service) => {
          if (service.show) {
            let valorCobertura = 0;
            let preco_beneficiario = 0;
            let valorCliente = 0;
            let precoSegurado = 0;

            if (tipoCobertura === "PARTICULAR") {
              valorCliente = service.tratamento.preco_particular;
            } else {
              const cobertura = service.tratamento.precosPorInstituicao?.find(
                (p) => p.instituicao === instituicaoSelecionada._id
              );

              if (cobertura?.coberto) {
                precoSegurado = cobertura.preco_segurado;
                if (modalidadePagamento === "DIRETO") {
                  // DIRETO: cliente paga só a parte dele (beneficiário)
                  valorCobertura = cobertura.comparticipacao;
                  valorCliente = cobertura.preco_beneficiario;
                  preco_beneficiario = cobertura.preco_beneficiario;
                } else {
                  // REEMBOLSO
                  // REEMBOLSO: cliente paga tudo (segurado)
                  valorCobertura = cobertura.comparticipacao;
                  valorCliente = cobertura.preco_segurado;
                  preco_beneficiario = cobertura.preco_beneficiario;
                }
              } else {
                valorCliente = service.tratamento.preco_particular;
              }
            }

            filteredServiceList.push({
              id: service.id,
              tratamento: service.tratamento.id,
              fornecedor:
                service.tratamento.fornecedor?._id ||
                service.tratamento.fornecedor ||
                null,
              dente: service.dente,
              faces: service.faces,
              preco_segurado: precoSegurado,
              valor_cobertura: valorCobertura,
              valor_cliente: valorCliente,
              preco_beneficiario: preco_beneficiario,
            });
          }
        });

        let avaliacaoCopia = avaliacao;

        serviceList.forEach((ser) => {
          avaliacaoCopia.procedimentos.forEach((avPr) => {
            if (ser.show && avPr.id === ser.id) {
              avPr.orcamentado = true;
            }
          });
        });

        try {
          const formData = new FormData();
          formData.append("cliente", avaliacao.cliente.id);
          formData.append(
            "medico_responsavel",
            avaliacao.medico_responsavel.id
          );
          formData.append("clinica", avaliacao.clinica.id);
          formData.append("avaliacao", avaliacao.id);
          formData.append("tipoCobertura", tipoCobertura);

          if (instituicaoSelecionada) {
            formData.append("instituicaoCobertura", instituicaoSelecionada._id);
            formData.append("modalidadePagamento", modalidadePagamento);
            if (instituicaoSelecionada.nome === "INPS") {
              formData.append("categoriaBeneficiario", categoriaBeneficiario);
            }
          }
          formData.append(
            "beneficio",
            tipoCobertura === "PARTICULAR"
              ? "Particular"
              : instituicaoSelecionada.nome
          );

          formData.append("procedimentos", JSON.stringify(filteredServiceList));
          formData.append("total", total);
          formData.append("subtotal", subtotal);
          formData.append("subtotalCliente", subtotalCliente);

          formData.append(
            "desconto",
            tipoDesconto === "$"
              ? desconto
              : modalidadePagamento === "REEMBOLSO_CLIENTE"
              ? Math.round((subtotalCliente * desconto) / 100)
              : Math.round((subtotal * desconto) / 100)
          );

          // Se tem desconto de parceria aplicado
          if (descontoParceriaAplicado && empresaParceira) {
            formData.append(
              "descontoParceria",
              JSON.stringify({
                empresaParceira: empresaParceira._id,
                nomeEmpresa: empresaParceira.nome,
                percentagemAplicada: empresaParceira.percentagemDesconto,
                valorDesconto:
                  tipoDesconto === "%"
                    ? Math.round((subtotal * desconto) / 100)
                    : desconto,
                dataAplicacao: new Date().toISOString(),
              })
            );
          }

          formData.append("tipoDesconto", tipoDesconto);
          formData.append(
            "diasValidadeParcela",
            clinica.clinica?.configuracoes?.configuracoes?.validadeParcela
              ?.validade || 15
          );

          if (tipoDesconto === "%") {
            formData.append("percentagemDescontoOriginal", desconto);
          }
          formData.append("observacoes", observacoes || "");
          formData.append("criadoPor", auth.userId);

          // Flags de controle
          formData.append("contaAprovada", contaAprovada);
          formData.append("terminado", false);
          formData.append("orcamentoIsentoPagamento", orcamentoIsentoPagamento);

          // Dados adicionais
          formData.append("caixaId", caixas[0]?._id);
          formData.append(
            "procedimentosAvaliacao",
            JSON.stringify(avaliacaoCopia.procedimentos)
          );

          // CHAMADA ÚNICA
          await sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos`,
            "POST",
            formData,
            {
              Authorization: "Bearer " + auth.token,
            }
          );

          toast.success(
            `Orçamento ${
              desconto === subtotal ? "isento de pagamento " : ""
            } adicionado com sucesso!`
          );
          navigate(`../../../clientes/${avaliacao.cliente.id}-orcamento`);
        } catch (err) {
          console.error("err", err);
        }
      } else {
        toast.error(formErrorMessage);
      }
    } catch (err) {
      console.error("err", err);
    } finally {
      setIsSubmitting(false);
      stopLoading();
    }
  };

  const submitHandlerDoisOrcamentos = async (descSegurado, descParticular) => {
    startLoading();
    try {
      const formErrorMessage = validarOrcamento(serviceList);
      if (formErrorMessage) {
        toast.error(formErrorMessage);
        setIsSubmitting(false);
        stopLoading();
        return;
      }

      // Separar procedimentos
      const procedimentosCobertos = [];
      const procedimentosNaoCobertos = [];

      serviceList.forEach((service) => {
        if (service.show) {
          const cobertura = service.tratamento.precosPorInstituicao?.find(
            (p) => p.instituicao === instituicaoSelecionada._id
          );

          const procedimento = {
            id: service.id,
            tratamento: service.tratamento.id,
            fornecedor:
              service.tratamento.fornecedor?._id ||
              service.tratamento.fornecedor ||
              null,
            dente: service.dente,
            faces: service.faces,
          };

          if (cobertura?.coberto) {
            procedimento.preco_segurado = cobertura.preco_segurado;
            procedimento.valor_cobertura = cobertura.comparticipacao;
            procedimento.valor_cliente =
              modalidadePagamento === "DIRETO"
                ? cobertura.preco_beneficiario
                : cobertura.preco_segurado;
            procedimento.preco_beneficiario = cobertura.preco_beneficiario;
            procedimentosCobertos.push(procedimento);
          } else {
            procedimento.preco_segurado = 0;
            procedimento.valor_cobertura = 0;
            procedimento.valor_cliente = service.tratamento.preco_particular;
            procedimento.preco_beneficiario = 0;
            procedimentosNaoCobertos.push(procedimento);
          }
        }
      });

      // Calcular totais
      const subtotalCobertos = procedimentosCobertos.reduce(
        (acc, p) => acc + p.valor_cliente,
        0
      );
      const subtotalNaoCobertos = procedimentosNaoCobertos.reduce(
        (acc, p) => acc + p.valor_cliente,
        0
      );

      // Usar os descontos já calculados que vêm como parâmetro
      const descontoCobertos = descSegurado;
      const descontoNaoCobertos = descParticular;

      const totalCobertos = subtotalCobertos - descontoCobertos;
      const totalNaoCobertos = subtotalNaoCobertos - descontoNaoCobertos;

      // Preparar avaliação atualizada
      let avaliacaoCopia = { ...avaliacao };
      avaliacaoCopia.procedimentos = avaliacaoCopia.procedimentos.map(
        (avPr) => {
          const foiOrcamentado = serviceList.some(
            (ser) => ser.show && avPr.id === ser.id
          );
          return {
            ...avPr,
            orcamentado: avPr.orcamentado || foiOrcamentado,
          };
        }
      );

      try {
        // 1ª Chamada - Orçamento SEGURADO
        const formData1 = new FormData();
        formData1.append("cliente", avaliacao.cliente.id);
        formData1.append("medico_responsavel", avaliacao.medico_responsavel.id);
        formData1.append("clinica", avaliacao.clinica.id);
        formData1.append("avaliacao", avaliacao.id);
        formData1.append("tipoCobertura", "SEGURADO");
        formData1.append("instituicaoCobertura", instituicaoSelecionada._id);
        formData1.append("modalidadePagamento", modalidadePagamento);
        formData1.append("beneficio", instituicaoSelecionada.nome);
        if (instituicaoSelecionada.nome === "INPS") {
          formData1.append("categoriaBeneficiario", categoriaBeneficiario);
        }
        formData1.append(
          "procedimentos",
          JSON.stringify(procedimentosCobertos)
        );
        formData1.append("total", totalCobertos);
        formData1.append("subtotal", subtotalCobertos);
        formData1.append("subtotalCliente", subtotalCobertos);
        formData1.append("desconto", descontoCobertos);
        formData1.append("tipoDesconto", tipoDesconto);
        if (tipoDesconto === "%" && descontoCobertos > 0) {
          const percentagemReal = (descontoCobertos / subtotalCobertos) * 100;
          formData1.append(
            "percentagemDescontoOriginal",
            percentagemReal.toFixed(2)
          );
        }
        formData1.append(
          "diasValidadeParcela",
          clinica.clinica?.configuracoes?.configuracoes?.validadeParcela
            ?.validade || 15
        );
        formData1.append("observacoes", observacoes || "");
        formData1.append("criadoPor", auth.userId);
        formData1.append("contaAprovada", false);
        formData1.append("terminado", false);
        formData1.append(
          "orcamentoIsentoPagamento",
          descontoCobertos >= subtotalCobertos
        );
        formData1.append("caixaId", caixas[0]?._id);

        if (descontoParceriaAplicado && empresaParceira && descSegurado > 0) {
          const descontoEsperado = Math.round(
            (subtotalCobertos * empresaParceira.percentagemDesconto) / 100
          );
          if (Math.abs(descSegurado - descontoEsperado) <= 10) {
            formData1.append(
              "descontoParceria",
              JSON.stringify({
                empresaParceira: empresaParceira._id,
                nomeEmpresa: empresaParceira.nome,
                percentagemAplicada: empresaParceira.percentagemDesconto,
                valorDesconto: descSegurado,
                dataAplicacao: new Date().toISOString(),
              })
            );
          }
        }

        formData1.append(
          "procedimentosAvaliacao",
          JSON.stringify(avaliacaoCopia.procedimentos)
        );

        await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos`,
          "POST",
          formData1,
          {
            Authorization: "Bearer " + auth.token,
          }
        );

        // 2ª Chamada - Orçamento PARTICULAR
        const formData2 = new FormData();
        formData2.append("cliente", avaliacao.cliente.id);
        formData2.append("medico_responsavel", avaliacao.medico_responsavel.id);
        formData2.append("clinica", avaliacao.clinica.id);
        formData2.append("avaliacao", avaliacao.id);
        formData2.append("tipoCobertura", "PARTICULAR");
        formData2.append("beneficio", "Particular");
        formData2.append(
          "procedimentos",
          JSON.stringify(procedimentosNaoCobertos)
        );
        formData2.append("total", totalNaoCobertos);
        formData2.append("subtotal", subtotalNaoCobertos);
        formData2.append("subtotalCliente", subtotalNaoCobertos);
        formData2.append("desconto", descontoNaoCobertos);
        formData2.append("tipoDesconto", tipoDesconto);
        if (tipoDesconto === "%" && descontoNaoCobertos > 0) {
          const percentagemReal =
            (descontoNaoCobertos / subtotalNaoCobertos) * 100;
          formData2.append(
            "percentagemDescontoOriginal",
            percentagemReal.toFixed(2)
          );
        }
        formData2.append(
          "diasValidadeParcela",
          clinica.clinica?.configuracoes?.configuracoes?.validadeParcela
            ?.validade || 15
        );
        formData2.append("observacoes", observacoes || "");
        formData2.append("criadoPor", auth.userId);
        formData2.append("contaAprovada", false);
        formData2.append("terminado", false);
        formData2.append(
          "orcamentoIsentoPagamento",
          descontoNaoCobertos >= subtotalNaoCobertos
        );
        formData2.append("caixaId", caixas[0]?._id);

        if (descontoParceriaAplicado && empresaParceira && descParticular > 0) {
          const descontoEsperado = Math.round(
            (subtotalNaoCobertos * empresaParceira.percentagemDesconto) / 100
          );
          if (Math.abs(descParticular - descontoEsperado) <= 10) {
            formData2.append(
              "descontoParceria",
              JSON.stringify({
                empresaParceira: empresaParceira._id,
                nomeEmpresa: empresaParceira.nome,
                percentagemAplicada: empresaParceira.percentagemDesconto,
                valorDesconto: descParticular,
                dataAplicacao: new Date().toISOString(),
              })
            );
          }
        }

        formData2.append(
          "procedimentosAvaliacao",
          JSON.stringify(avaliacaoCopia.procedimentos)
        );

        await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos`,
          "POST",
          formData2,
          {
            Authorization: "Bearer " + auth.token,
          }
        );

        toast.success("2 Orçamentos criados com sucesso!");
        navigate(`../../../clientes/${avaliacao.cliente.id}-orcamento`);
      } catch (err) {
        console.error("err", err);
        toast.error("Erro ao criar orçamentos");
      }
    } catch (err) {
      console.error("err", err);
    } finally {
      setIsSubmitting(false);
      stopLoading();
    }
  };

  const removeService = (id) => {
    const list = serviceList;
    const servico = list.filter((el) => el.id === id);
    const indice = list.indexOf(servico[0]);

    list[indice].show = false;
    setServiceList([...list]);

    updateValores(instituicaoSelecionada?._id || "PARTICULAR");
  };

  const addService = (id) => {
    const list = serviceList;
    const servico = list.filter((el) => el.id === id);
    const indice = list.indexOf(servico[0]);

    list[indice].show = true;
    setServiceList([...list]);

    updateValores(instituicaoSelecionada?._id || "PARTICULAR");
  };

  // Variantes (por fornecedor) que partilham a mesma designação de um tratamento.
  const getVariantesFornecedor = (tratamento) => {
    if (!tratamento) return [];
    return todosTratamentos.filter(
      (t) => t.designacao === tratamento.designacao
    );
  };

  // Trocar a variante (fornecedor) escolhida para uma linha do orçamento,
  // recalculando os valores. A avaliação não é afetada (continua agnóstica
  // ao fornecedor); apenas o orçamento usa o preço da variante escolhida.
  const handleSelecionarFornecedor = (serviceId, tratamentoId) => {
    const list = serviceList;
    const indice = list.findIndex((el) => el.id === serviceId);
    if (indice === -1) return;

    const novoTratamento = todosTratamentos.find((t) => t.id === tratamentoId);
    if (!novoTratamento) return;

    list[indice] = { ...list[indice], tratamento: novoTratamento };
    setServiceList([...list]);

    updateValores(instituicaoSelecionada?._id || "PARTICULAR");
  };

  const updateValores = (instituicaoIdOuParticular) => {
    if (!serviceList) return;

    const isParticular = instituicaoIdOuParticular === "PARTICULAR";

    let tempTotalCobertura = 0;

    const tempServiceList = serviceList.map((el) => {
      if (el.show === true) {
        let valorTotal;

        const cobertura = isParticular
          ? 0
          : el.tratamento.precosPorInstituicao?.find(
              (p) => p.instituicao === instituicaoIdOuParticular
            );

        tempTotalCobertura += cobertura?.comparticipacao || 0;

        if (isParticular) {
          valorTotal = el.tratamento.preco_particular;
        } else {
          const cobertura = el.tratamento.precosPorInstituicao?.find(
            (p) => p.instituicao === instituicaoIdOuParticular
          );

          if (cobertura?.coberto) {
            valorTotal =
              modalidadePagamento === "DIRETO"
                ? cobertura.preco_beneficiario
                : cobertura.preco_segurado;
          } else {
            valorTotal = el.tratamento.preco_particular;
          }
        }

        return {
          ...el,
          valorTotal: valorTotal,
        };
      }
      return el;
    });

    const filteredServiceList = tempServiceList.filter(
      (el) => el.show === true
    );
    setServiceList(tempServiceList);
    const tempSubTotal = filteredServiceList.reduce((acc, el) => {
      return acc + (el.valorTotal || 0);
    }, 0);
    setTotalCobertura(tempTotalCobertura);
    setSubtotal(tempSubTotal);

    let tempSubtotalCliente = 0;

    if (modalidadePagamento === "REEMBOLSO_CLIENTE" && isParticular === false) {
      tempSubtotalCliente = tempSubTotal - tempTotalCobertura;
      setSubtotalCliente(tempSubtotalCliente);
    } else {
      tempSubtotalCliente = tempSubTotal;
      setSubtotalCliente(tempSubTotal);
    }

    handleIsencaoPagamento(desconto, tipoDesconto, tempSubTotal);
    calculateTotalWithDesconto(
      desconto,
      tipoDesconto,
      tempSubTotal,
      tempSubtotalCliente,
      isParticular
    );
  };

  const handleIsencaoPagamento = (desc, tipoDesc, subTo) => {
    if (
      (tipoDesc === "%" && desc >= 100) ||
      (tipoDesc === "$" && desc >= subTo)
    ) {
      setOrcamentoIsentoPagamento(true);
    } else {
      setOrcamentoIsentoPagamento(false);
    }
  };

  const changeDescontoHandler = (e) => {
    handleIsencaoPagamento(e.target.value, tipoDesconto, subtotal);

    if (e.target.value < 0) {
      setDesconto(0);
      calculateTotalWithDesconto(0, tipoDesconto, subtotal, subtotalCliente);
    } else if (tipoDesconto === "%" && e.target.value > 100) {
      setDesconto(100);
      calculateTotalWithDesconto(100, tipoDesconto, subtotal, subtotalCliente);
    } else if (tipoDesconto === "$" && e.target.value > subtotalCliente) {
      setDesconto(subtotalCliente);
      calculateTotalWithDesconto(
        subtotalCliente,
        tipoDesconto,
        subtotal,
        subtotalCliente
      );
    } else {
      setDesconto(e.target.value);
      calculateTotalWithDesconto(
        e.target.value,
        tipoDesconto,
        subtotal,
        subtotalCliente
      );
    }

    if (descontoParceriaAplicado) {
      if (
        tipoDesconto !== "%" ||
        Number(e.target.value) !== empresaParceira?.percentagemDesconto
      ) {
        setDescontoParceriaAplicado(false);
      }
    }
  };

  const changeTipoDescontoHandler = (e) => {
    handleIsencaoPagamento(desconto, e.target.value, subtotal);
    setTipoDesconto(e.target.value);

    if (e.target.value === "%" && desconto > 100) {
      setDesconto(100);
      calculateTotalWithDesconto(
        100,
        e.target.value,
        subtotal,
        subtotalCliente
      );
    } else {
      calculateTotalWithDesconto(
        desconto,
        e.target.value,
        subtotal,
        subtotalCliente
      );
    }

    if (descontoParceriaAplicado) {
      if (
        e.target.value !== "%" ||
        desconto !== empresaParceira?.percentagemDesconto
      ) {
        setDescontoParceriaAplicado(false);
      }
    }
  };

  const calculateTotalWithDesconto = (
    desconto,
    tipoDesconto,
    subtotal,
    tempSubtotalCliente,
    isParticular
  ) => {
    if (tipoDesconto === "$") {
      setTotal(subtotal - desconto);
    }

    if (tipoDesconto === "%") {
      if (
        modalidadePagamento === "REEMBOLSO_CLIENTE" &&
        isParticular !== true
      ) {
        setTotal(Math.round(subtotal - (desconto / 100) * tempSubtotalCliente));
      } else {
        setTotal(Math.round(subtotal - (desconto / 100) * subtotal));
      }
    }
  };

  const preSubmitHandler = (aprovar) => {
    if (!isSubmitting) {
      if (serviceList.filter((el) => el.show === true).length <= 0) {
        toast.error("Nenhum tratamento selecionado");
      } else {
        setIsSubmitting(true);
        submitHandler(aprovar);
      }
    }
  };

  const preSubmitHandlerDoisOrcamentos = () => {
    if (!isSubmitting) {
      if (serviceList.filter((el) => el.show === true).length <= 0) {
        toast.error("Nenhum tratamento selecionado");
        return;
      }

      if (desconto > 0) {
        // Calcular totais
        const { subtotalCobertos, subtotalNaoCobertos } =
          calcularTotaisOrcamentos();

        // Converter desconto para escudos se for percentual
        const descontoEmEscudos =
          tipoDesconto === "$"
            ? desconto
            : Math.round(
                ((subtotalCobertos + subtotalNaoCobertos) * desconto) / 100
              );

        // Inicializar modal com 100% no particular (opção padrão)
        setDescontoParticular(descontoEmEscudos);
        setDescontoSegurado(0);
        setOpcaoRapidaSelecionada("particular");
        setModalDescontoAberta(true);
      } else {
        setIsSubmitting(true);
        submitHandlerDoisOrcamentos(0, 0);
      }
    }
  };

  const calcularTotaisOrcamentos = () => {
    let subtotalCobertos = 0;
    let subtotalNaoCobertos = 0;

    serviceList.forEach((service) => {
      if (service.show) {
        const cobertura = service.tratamento.precosPorInstituicao?.find(
          (p) => p.instituicao === instituicaoSelecionada._id
        );

        if (cobertura?.coberto) {
          subtotalCobertos +=
            modalidadePagamento === "DIRETO"
              ? cobertura.preco_beneficiario
              : cobertura.preco_segurado;
        } else {
          subtotalNaoCobertos += service.tratamento.preco_particular;
        }
      }
    });

    return { subtotalCobertos, subtotalNaoCobertos };
  };

  const aplicarDescontoRapido = (opcao) => {
    const { subtotalCobertos, subtotalNaoCobertos } =
      calcularTotaisOrcamentos();

    const descontoEmEscudos =
      tipoDesconto === "$"
        ? desconto
        : Math.round(
            ((subtotalCobertos + subtotalNaoCobertos) * desconto) / 100
          );

    setOpcaoRapidaSelecionada(opcao);

    switch (opcao) {
      case "particular":
        setDescontoParticular(descontoEmEscudos);
        setDescontoSegurado(0);
        break;
      case "segurado":
        setDescontoSegurado(descontoEmEscudos);
        setDescontoParticular(0);
        break;
      case "proporcional":
        const totalGeral = subtotalCobertos + subtotalNaoCobertos;
        const descSegurado = Math.round(
          (descontoEmEscudos * subtotalCobertos) / totalGeral
        );
        const descParticular = Math.round(
          (descontoEmEscudos * subtotalNaoCobertos) / totalGeral
        );
        setDescontoSegurado(descSegurado);
        setDescontoParticular(descParticular);
        break;
      case "limpar":
        setDescontoSegurado(0);
        setDescontoParticular(0);
        break;
      default:
        break;
    }
  };

  const handleDescontoManualChange = (tipo, valor) => {
    setOpcaoRapidaSelecionada(null); // Remove seleção de botão rápido

    const valorNumerico = Math.max(0, Math.floor(Number(valor) || 0));

    if (tipo === "segurado") {
      setDescontoSegurado(valorNumerico);
    } else {
      setDescontoParticular(valorNumerico);
    }
  };

  return (
    <>
      <div className="new">
        <Sidebar />
        <div className="newContainer">
          {avaliacao && (
            <Navbar
              title="Orçamento"
              icon={ArticleOutlinedIcon}
              paths={[
                {
                  nome: avaliacao.cliente.nome,
                  link: `../../../clientes/${avaliacao.cliente.id}-orcamento`,
                },
                { nome: "Novo" },
              ]}
            />
          )}

          {avaliacao?.ativo === false || avaliacao?.ativo === "false" ? (
            <div className="avaliacaoDesativadaErrorMsg">
              Este orçamento não pode ser criado pois a avaliação correspondente
              se encontra desativada!
            </div>
          ) : tratamentosEmFalta.length > 0 ? (
            /* Recusa-se em vez de orçamentar ao preço da outra unidade. A
               alternativa — deixar seguir com o tratamento de lá — produzia uma
               venda carimbada nesta unidade e cobrada à tabela da outra. */
            <div className="avaliacaoDesativadaErrorMsg">
              Este orçamento não pode ser criado nesta unidade: a avaliação foi
              feita noutra unidade e{" "}
              {tratamentosEmFalta.length === 1
                ? "o tratamento seguinte não existe"
                : "os tratamentos seguintes não existem"}{" "}
              no catálogo daqui —{" "}
              <strong>{tratamentosEmFalta.join(", ")}</strong>. Orçamente na
              unidade onde a avaliação foi feita, ou peça que o tratamento seja
              acrescentado ao catálogo desta unidade.
            </div>
          ) : (
            <>
              {serviceList && avaliacao && (
                <div className="bottom-orcamento caixa">
                  <div className="pOrcamento-header">
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{ maxWidth: "300px" }}
                    >
                      <InputLabel id="label-cobertura">Cobertura</InputLabel>
                      <Select
                        labelId="label-cobertura"
                        id="select_cobertura"
                        value={instituicaoSelecionada?._id || "PARTICULAR"}
                        label="Cobertura"
                        onChange={handleInstituicaoChange}
                      >
                        <MenuItem value="PARTICULAR">Particular</MenuItem>
                        {instituicoes.map((inst) => (
                          <MenuItem value={inst._id} key={inst._id}>
                            {inst.nome}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {instituicaoSelecionada?.nome === "INPS" && (
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{ maxWidth: "300px" }}
                      >
                        <InputLabel id="label-categoria">
                          Categoria Beneficiário
                        </InputLabel>
                        <Select
                          labelId="label-categoria"
                          id="select_categoria"
                          value={categoriaBeneficiario}
                          label="Categoria"
                          onChange={(e) => {
                            setCategoriaBeneficiario(e.target.value);
                          }}
                        >
                          <MenuItem value="">Selecione uma categoria</MenuItem>
                          <MenuItem value="TCP">
                            Trabalhado Conta Própria
                          </MenuItem>
                          <MenuItem value="TCO">
                            Trabalhado Conta Outrem
                          </MenuItem>
                          <MenuItem value="PA">Função Pública</MenuItem>
                          <MenuItem value="MBCSS">
                            Migrantes - Beneficiários Convenções Segurança
                            Social
                          </MenuItem>
                        </Select>
                      </FormControl>
                    )}

                    {instituicaoSelecionada && (
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{ maxWidth: "300px" }}
                      >
                        <InputLabel id="label-modalidade">
                          Modalidade
                        </InputLabel>
                        <Select
                          labelId="label-modalidade"
                          id="select_modalidade"
                          value={modalidadePagamento}
                          label="Modalidade"
                          onChange={(e) => {
                            setModalidadePagamento(e.target.value);
                            updateValores(instituicaoSelecionada._id);
                          }}
                        >
                          <MenuItem value="DIRETO">Direto</MenuItem>
                          <MenuItem value="REEMBOLSO_CLIENTE">
                            Reembolso
                          </MenuItem>
                        </Select>
                      </FormControl>
                    )}

                    <div className="pOrcamento-header__item">
                      <label className="label">Médico Avaliador:</label>
                      <span className="input">
                        {avaliacao.medico_responsavel.name}
                      </span>
                    </div>
                  </div>

                  {parceriaValida && empresaParceira && (
                    <div
                      style={{
                        backgroundColor: "#e8f5e9",
                        border: "1px solid #4caf50",
                        borderRadius: "8px",
                        padding: "15px 20px",
                        marginBottom: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "15px",
                        }}
                      >
                        <BusinessIcon
                          style={{ color: "#4caf50", fontSize: "28px" }}
                        />
                        <div>
                          <div
                            style={{
                              fontWeight: "600",
                              color: "#2e7d32",
                              fontSize: "15px",
                            }}
                          >
                            Cliente de Empresa Parceira
                          </div>
                          <div style={{ color: "#388e3c", fontSize: "14px" }}>
                            <strong>{empresaParceira.nome}</strong> - Desconto
                            de{" "}
                            <strong>
                              {empresaParceira.percentagemDesconto}%
                            </strong>{" "}
                            disponível
                          </div>
                        </div>
                      </div>

                      {!descontoParceriaAplicado ? (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => {
                            setDesconto(empresaParceira.percentagemDesconto);
                            setTipoDesconto("%");
                            setDescontoParceriaAplicado(true);
                            calculateTotalWithDesconto(
                              empresaParceira.percentagemDesconto,
                              "%",
                              subtotal,
                              subtotalCliente,
                              tipoCobertura === "PARTICULAR"
                            );
                            toast.success(
                              `Desconto de ${empresaParceira.percentagemDesconto}% aplicado!`
                            );
                          }}
                        >
                          Aplicar Desconto
                        </Button>
                      ) : (
                        <div
                          style={{
                            backgroundColor: "#4caf50",
                            color: "white",
                            padding: "6px 16px",
                            borderRadius: "4px",
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          ✓ Desconto Aplicado
                        </div>
                      )}
                    </div>
                  )}

                  {instituicaoSelecionada && (
                    <span className="tituloListaProcedimentos">
                      {`Lista de tratamentos - ${instituicaoSelecionada.nome} (${nrTratamentosCobertos})`}
                    </span>
                  )}

                  <div
                    className={`orcamento__item orcamento__item--headerDiv ${
                      tipoCobertura === "PARTICULAR"
                        ? "orcamento__item--particular"
                        : ""
                    }`}
                  >
                    <span className="orcamento__item--header">Tratamento</span>
                    <span className="orcamento__item--header">Val. Unit.</span>
                    {tipoCobertura !== "PARTICULAR" && (
                      <span className="orcamento__item--header">
                        {instituicaoSelecionada?.nome}
                      </span>
                    )}
                    {tipoCobertura !== "PARTICULAR" && (
                      <span className="orcamento__item--header">
                        Beneficiário
                      </span>
                    )}
                  </div>

                  {tipoCobertura === "PARTICULAR" &&
                    serviceList.map((singleService, i) => (
                      <div
                        key={i}
                        className="orcamento__item orcamento__item--particular"
                      >
                        <ElementoOrcamento
                          dente={singleService.dente}
                          faces={singleService.faces}
                          tratamento={singleService.tratamento}
                          tipoCobertura={tipoCobertura}
                          instituicao={null}
                          modalidadePagamento={null}
                          removeService={removeService}
                          addService={addService}
                          id={singleService.id}
                          show={singleService.show}
                          variantes={getVariantesFornecedor(
                            singleService.tratamento
                          )}
                          onSelecionarFornecedor={(tratId) =>
                            handleSelecionarFornecedor(singleService.id, tratId)
                          }
                        />
                      </div>
                    ))}

                  {/* Lista para tratamentos cobertos pela instituição */}
                  {tipoCobertura !== "PARTICULAR" &&
                    serviceList.map((singleService, i) => {
                      const cobertura =
                        singleService.tratamento.precosPorInstituicao?.find(
                          (p) => p.instituicao === instituicaoSelecionada._id
                        );

                      if (cobertura?.coberto) {
                        return (
                          <div key={i} className="orcamento__item">
                            <ElementoOrcamento
                              dente={singleService.dente}
                              faces={singleService.faces}
                              tratamento={singleService.tratamento}
                              tipoCobertura={tipoCobertura}
                              instituicao={instituicaoSelecionada}
                              modalidadePagamento={modalidadePagamento}
                              cobertura={cobertura}
                              removeService={removeService}
                              addService={addService}
                              id={singleService.id}
                              show={singleService.show}
                              variantes={getVariantesFornecedor(
                                singleService.tratamento
                              )}
                              onSelecionarFornecedor={(tratId) =>
                                handleSelecionarFornecedor(
                                  singleService.id,
                                  tratId
                                )
                              }
                            />
                          </div>
                        );
                      }
                      return null;
                    })}

                  {/* Lista para tratamentos NÃO cobertos */}
                  {tipoCobertura !== "PARTICULAR" && (
                    <>
                      <span className="tituloListaProcedimentos tituloListaProcedimentos--2">
                        Lista de tratamentos - PARTICULAR (
                        {nrTratamentosNaoCobertos})
                      </span>
                      {serviceList.map((singleService, i) => {
                        const cobertura =
                          singleService.tratamento.precosPorInstituicao?.find(
                            (p) => p.instituicao === instituicaoSelecionada._id
                          );

                        if (!cobertura?.coberto) {
                          return (
                            <div key={i} className="orcamento__item">
                              <ElementoOrcamento
                                dente={singleService.dente}
                                faces={singleService.faces}
                                tratamento={singleService.tratamento}
                                tipoCobertura={tipoCobertura}
                                instituicao={instituicaoSelecionada}
                                modalidadePagamento={modalidadePagamento}
                                removeService={removeService}
                                addService={addService}
                                id={singleService.id}
                                show={singleService.show}
                                tratamentoNaoCoberto={true}
                                variantes={getVariantesFornecedor(
                                  singleService.tratamento
                                )}
                                onSelecionarFornecedor={(tratId) =>
                                  handleSelecionarFornecedor(
                                    singleService.id,
                                    tratId
                                  )
                                }
                              />
                            </div>
                          );
                        }
                        return null;
                      })}
                    </>
                  )}

                  {/* Procedimentos já orçamentados */}
                  {procedimentosOrcamentados.map((pr, i) => (
                    <div key={i} className="orcamento__item">
                      <ElementoOrcamentoOrcamentados procedimento={pr} />
                    </div>
                  ))}

                  <span className="orcamento__areaPagamento__titulo">
                    Pagamento
                  </span>
                  <div className="orcamento__areaPagamento__container">
                    <div className="orcamento__areaPagamento__container--1">
                      <span className="orcamento__areaPagamento__tituloDesconto">
                        Desconto
                      </span>
                      <div className="orcamento__areaPagamento__desconto">
                        <select
                          onChange={changeTipoDescontoHandler}
                          value={tipoDesconto}
                        >
                          <option value="$">$00</option>
                          <option value="%">%</option>
                        </select>
                        <input
                          type="number"
                          min={0}
                          value={desconto}
                          onChange={(e) => {
                            const value = Math.floor(Number(e.target.value));
                            if (!isNaN(value)) {
                              changeDescontoHandler({
                                target: { value: value },
                              });
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === ".") {
                              e.preventDefault();
                            }
                          }}
                          step={1}
                        />
                      </div>

                      <span className="orcamento__areaPagamento__tituloDesconto">
                        Observação
                      </span>
                      <textarea
                        name=""
                        id=""
                        cols="30"
                        rows="7"
                        value={observacoes}
                        onChange={(e) => setObservacoes(e.target.value)}
                      ></textarea>
                    </div>
                    <div className="orcamento__areaPagamento__container--3"></div>

                    <div className="orcamento__areaPagamento__container--2">
                      <span className="orcamento__areaPagamento__container--2__titulo">
                        Detalhes sobre o pagamento
                      </span>
                      <div className="orcamento__areaPagamento__container--2__line">
                        <span>Subtotal:</span>
                        <span>
                          {Intl.NumberFormat("pt-BR").format(subtotal)}$00
                        </span>
                      </div>

                      {modalidadePagamento === "REEMBOLSO_CLIENTE" && (
                        <>
                          <div className="orcamento__areaPagamento__container--2__line">
                            <span>Total Reembolso:</span>
                            <span>
                              {Intl.NumberFormat("pt-BR").format(
                                totalCobertura
                              )}
                              $00
                            </span>
                          </div>

                          <div className="orcamento__areaPagamento__container--2__line">
                            <span>Total Beneficiário:</span>
                            <span>
                              {Intl.NumberFormat("pt-BR").format(
                                subtotalCliente
                              )}
                              $00
                            </span>
                          </div>
                        </>
                      )}

                      {tipoCobertura === "MISTO" && instituicaoSelecionada && (
                        <>
                          {modalidadePagamento !== "REEMBOLSO_CLIENTE" && (
                            <div className="orcamento__areaPagamento__container--2__line">
                              <span>Total {instituicaoSelecionada.nome}:</span>
                              <span>
                                {Intl.NumberFormat("pt-BR").format(
                                  serviceList
                                    .filter((s) => s.show && s.tratamento)
                                    .filter(
                                      (s) =>
                                        s.tratamento.precosPorInstituicao?.find(
                                          (p) =>
                                            p.instituicao ===
                                            instituicaoSelecionada._id
                                        )?.coberto
                                    )
                                    .reduce((acc, s) => {
                                      const cob =
                                        s.tratamento.precosPorInstituicao.find(
                                          (p) =>
                                            p.instituicao ===
                                            instituicaoSelecionada._id
                                        );
                                      return (
                                        acc +
                                        (modalidadePagamento === "DIRETO"
                                          ? cob.preco_beneficiario
                                          : cob.preco_segurado)
                                      );
                                    }, 0)
                                )}
                                $00
                              </span>
                            </div>
                          )}
                          <div className="orcamento__areaPagamento__container--2__line">
                            <span>Total Particular:</span>
                            <span>
                              {Intl.NumberFormat("pt-BR").format(
                                serviceList
                                  .filter((s) => s.show && s.tratamento)
                                  .filter(
                                    (s) =>
                                      !s.tratamento.precosPorInstituicao?.find(
                                        (p) =>
                                          p.instituicao ===
                                          instituicaoSelecionada._id
                                      )?.coberto
                                  )
                                  .reduce(
                                    (acc, s) =>
                                      acc + s.tratamento.preco_particular,
                                    0
                                  )
                              )}
                              $00
                            </span>
                          </div>
                        </>
                      )}

                      <div className="orcamento__areaPagamento__container--2__line">
                        <span>Desconto:</span>
                        <span>
                          {Intl.NumberFormat("pt-BR").format(
                            tipoDesconto === "$"
                              ? desconto
                              : modalidadePagamento === "REEMBOLSO_CLIENTE"
                              ? Math.round((subtotalCliente * desconto) / 100)
                              : Math.round((subtotal * desconto) / 100)
                          )}
                          $00
                        </span>
                      </div>
                      <div className="orcamento__areaPagamento__container--2__line">
                        <span className="orcamento__areaPagamento__container--2__line__title-big">
                          Total
                        </span>
                        <span>
                          {Intl.NumberFormat("pt-BR").format(total)}$00
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="botoes">
                    <button
                      className="cancel-btn"
                      onClick={() =>
                        navigate(
                          `../../../clientes/${avaliacao.cliente.id}-orcamento`
                        )
                      }
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={"blue-button"}
                      onClick={preSubmitHandler.bind(null, false)}
                    >
                      {isSubmitting ? "Criando..." : "Criar"}
                    </button>

                    {nrTratamentosNaoCobertos > 0 &&
                      nrTratamentosCobertos > 0 && (
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="blue-button"
                          onClick={preSubmitHandlerDoisOrcamentos}
                        >
                          {isSubmitting ? "Criando..." : "Criar 2 Orçamentos"}
                        </button>
                      )}

                    {tipoCobertura === "PARTICULAR" && (
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="blue-button"
                        onClick={preSubmitHandler.bind(null, true)}
                      >
                        {isSubmitting ? "Criando..." : "Criar e Aprovar"}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal de escolha de desconto */}
        {modalDescontoAberta &&
          (() => {
            const { subtotalCobertos, subtotalNaoCobertos } =
              calcularTotaisOrcamentos();
            const totalGeral = subtotalCobertos + subtotalNaoCobertos;

            const descontoEmEscudos =
              tipoDesconto === "$"
                ? desconto
                : Math.round((totalGeral * desconto) / 100);

            const totalDistribuido = descontoSegurado + descontoParticular;
            const diferenca = descontoEmEscudos - totalDistribuido;

            const erroSegurado = descontoSegurado > subtotalCobertos;
            const erroParticular = descontoParticular > subtotalNaoCobertos;
            const avisoExcesso = totalDistribuido > descontoEmEscudos;

            const totalFinalSegurado = subtotalCobertos - descontoSegurado;
            const totalFinalParticular =
              subtotalNaoCobertos - descontoParticular;

            const temErro = erroSegurado || erroParticular;

            return (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 9999,
                }}
                onClick={() => {
                  setModalDescontoAberta(false);
                  setOpcaoRapidaSelecionada(null);
                }}
              >
                <div
                  style={{
                    backgroundColor: "white",
                    padding: "30px",
                    borderRadius: "8px",
                    maxWidth: "600px",
                    width: "90%",
                    maxHeight: "90vh",
                    overflow: "auto",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 style={{ marginBottom: "20px", marginTop: 0 }}>
                    Distribuir Desconto de{" "}
                    {Intl.NumberFormat("pt-BR").format(descontoEmEscudos)}$00
                  </h3>

                  {/* Informações dos orçamentos */}
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      padding: "15px",
                      borderRadius: "6px",
                      marginBottom: "20px",
                      fontSize: "14px",
                    }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <strong>Orçamento {instituicaoSelecionada?.nome}:</strong>{" "}
                      {Intl.NumberFormat("pt-BR").format(subtotalCobertos)}$00
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <strong>Orçamento Particular:</strong>{" "}
                      {Intl.NumberFormat("pt-BR").format(subtotalNaoCobertos)}
                      $00
                    </div>
                    <div>
                      <strong>Total:</strong>{" "}
                      {Intl.NumberFormat("pt-BR").format(totalGeral)}$00
                    </div>
                  </div>

                  {/* Botões de aplicação rápida */}
                  <div style={{ marginBottom: "25px" }}>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        marginBottom: "10px",
                        color: "#666",
                      }}
                    >
                      Aplicação Rápida:
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => aplicarDescontoRapido("particular")}
                        style={{
                          padding: "10px 15px",
                          border: "2px solid",
                          borderColor:
                            opcaoRapidaSelecionada === "particular"
                              ? "#2196F3"
                              : "#ddd",
                          backgroundColor:
                            opcaoRapidaSelecionada === "particular"
                              ? "#e3f2fd"
                              : "white",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "13px",
                          fontWeight: "500",
                        }}
                      >
                        100% Particular
                      </button>
                      <button
                        type="button"
                        onClick={() => aplicarDescontoRapido("segurado")}
                        style={{
                          padding: "10px 15px",
                          border: "2px solid",
                          borderColor:
                            opcaoRapidaSelecionada === "segurado"
                              ? "#2196F3"
                              : "#ddd",
                          backgroundColor:
                            opcaoRapidaSelecionada === "segurado"
                              ? "#e3f2fd"
                              : "white",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "13px",
                          fontWeight: "500",
                        }}
                      >
                        100% {instituicaoSelecionada?.nome}
                      </button>
                      <button
                        type="button"
                        onClick={() => aplicarDescontoRapido("proporcional")}
                        style={{
                          padding: "10px 15px",
                          border: "2px solid",
                          borderColor:
                            opcaoRapidaSelecionada === "proporcional"
                              ? "#2196F3"
                              : "#ddd",
                          backgroundColor:
                            opcaoRapidaSelecionada === "proporcional"
                              ? "#e3f2fd"
                              : "white",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "13px",
                          fontWeight: "500",
                        }}
                      >
                        Proporcional
                      </button>
                      <button
                        type="button"
                        onClick={() => aplicarDescontoRapido("limpar")}
                        style={{
                          padding: "10px 15px",
                          border: "2px solid #ddd",
                          backgroundColor: "white",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "13px",
                          fontWeight: "500",
                        }}
                      >
                        Limpar
                      </button>
                    </div>
                  </div>

                  {/* Inputs manuais */}
                  <div style={{ marginBottom: "20px" }}>
                    <div style={{ marginBottom: "20px" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        Desconto Orçamento {instituicaoSelecionada?.nome}:
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <input
                          type="number"
                          min={0}
                          value={descontoSegurado}
                          onChange={(e) =>
                            handleDescontoManualChange(
                              "segurado",
                              e.target.value
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === ".") e.preventDefault();
                          }}
                          style={{
                            flex: 1,
                            padding: "10px",
                            border: `2px solid ${
                              erroSegurado ? "#f44336" : "#ddd"
                            }`,
                            borderRadius: "6px",
                            fontSize: "14px",
                          }}
                        />
                        <span style={{ fontSize: "14px", color: "#666" }}>
                          $00
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginTop: "5px",
                        }}
                      >
                        Máximo:{" "}
                        {Intl.NumberFormat("pt-BR").format(subtotalCobertos)}$00
                      </div>
                      {erroSegurado && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#f44336",
                            marginTop: "5px",
                            fontWeight: "500",
                          }}
                        >
                          ⚠️ Desconto não pode ser maior que o valor do
                          orçamento
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        Desconto Orçamento Particular:
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <input
                          type="number"
                          min={0}
                          value={descontoParticular}
                          onChange={(e) =>
                            handleDescontoManualChange(
                              "particular",
                              e.target.value
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === ".") e.preventDefault();
                          }}
                          style={{
                            flex: 1,
                            padding: "10px",
                            border: `2px solid ${
                              erroParticular ? "#f44336" : "#ddd"
                            }`,
                            borderRadius: "6px",
                            fontSize: "14px",
                          }}
                        />
                        <span style={{ fontSize: "14px", color: "#666" }}>
                          $00
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginTop: "5px",
                        }}
                      >
                        Máximo:{" "}
                        {Intl.NumberFormat("pt-BR").format(subtotalNaoCobertos)}
                        $00
                      </div>
                      {erroParticular && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#f44336",
                            marginTop: "5px",
                            fontWeight: "500",
                          }}
                        >
                          ⚠️ Desconto não pode ser maior que o valor do
                          orçamento
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Resumo */}
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      padding: "15px",
                      borderRadius: "6px",
                      marginBottom: "20px",
                      fontSize: "14px",
                    }}
                  >
                    <div
                      style={{
                        marginBottom: "10px",
                        paddingBottom: "10px",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      <strong>Total distribuído:</strong>{" "}
                      {Intl.NumberFormat("pt-BR").format(totalDistribuido)}$00
                    </div>

                    {diferenca !== 0 && (
                      <div
                        style={{
                          marginBottom: "10px",
                          paddingBottom: "10px",
                          borderBottom: "1px solid #ddd",
                          color: avisoExcesso ? "#ff9800" : "#666",
                        }}
                      >
                        {avisoExcesso ? "⚠️ Excesso:" : "ℹ️ Restante:"}{" "}
                        {Intl.NumberFormat("pt-BR").format(Math.abs(diferenca))}
                        $00
                      </div>
                    )}

                    <div style={{ marginBottom: "5px" }}>
                      <strong>Totais finais:</strong>
                    </div>
                    <div style={{ marginLeft: "10px" }}>
                      <div>
                        • {instituicaoSelecionada?.nome}:{" "}
                        {Intl.NumberFormat("pt-BR").format(totalFinalSegurado)}
                        $00
                      </div>
                      <div>
                        • Particular:{" "}
                        {Intl.NumberFormat("pt-BR").format(
                          totalFinalParticular
                        )}
                        $00
                      </div>
                    </div>
                  </div>

                  {/* Botões de ação */}
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      className="cancel-btn"
                      onClick={() => {
                        setModalDescontoAberta(false);
                        setOpcaoRapidaSelecionada(null);
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      className="blue-button"
                      disabled={temErro}
                      onClick={() => {
                        setModalDescontoAberta(false);
                        setIsSubmitting(true);
                        submitHandlerDoisOrcamentos(
                          descontoSegurado,
                          descontoParticular
                        );
                      }}
                      style={{
                        opacity: temErro ? 0.5 : 1,
                        cursor: temErro ? "not-allowed" : "pointer",
                      }}
                    >
                      Confirmar
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
      </div>
    </>
  );
}

export default NewOrcamento;
